/**
 * ActionQueue — the new admin landing cockpit (iter114s.75).
 *
 * Replaces the old tabbed AdminDashboard as the default `/admin` route.
 * Architecture per operator brief:
 *
 *   1. Priority Bucket cards (clickable, above the fold)
 *   2. Live Production Pipeline (horizontal kanban)
 *   3. Universal Smart Queue (filter chips + mission-control rows)
 *
 * All three slices come from a single `GET /api/admin/action-queue?queue=...`
 * round-trip so chip-clicks don't introduce a render race between
 * bucket counts and queue rows.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { AdminLayout } from './AdminLayout';
import { PriorityBucketCard } from './ActionQueue/PriorityBucketCard';
import { LivePipelineKanban } from './ActionQueue/LivePipelineKanban';
import { SmartQueueFilterChips } from './ActionQueue/SmartQueueFilterChips';
import { MissionControlOrderRow } from './ActionQueue/MissionControlOrderRow';
import { SavedViewsBar } from './ActionQueue/SavedViewsBar';
import { OrderManagementModal } from './OrderManagementModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ActionQueue = () => {
  const { user, isAdmin, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [queue, setQueue] = useState('new');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      navigate('/');
    }
  }, [authLoading, user, isAdmin, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/api/admin/action-queue`, {
          params: { queue, page: 1 },
          withCredentials: true,
        });
        if (cancelled) return;
        setData(res.data);
        setError('');
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Unable to load action queue.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [queue, refreshTick]);

  // When a priority bucket is clicked we map it to the matching
  // smart-queue chip — the bucket card and the chip are two sides of
  // the same intent.
  const handleBucketClick = (bucketId) => {
    const map = {
      new_paid: 'new',
      proof_revisions: 'proof_revisions',
      files_failed: 'files_failed',
      awaiting_supplier: 'awaiting_supplier',
      tracking_missing: 'tracking_missing',
      custom_quotes: 'custom_quotes',
      operator_alerts: 'stuck',
    };
    const next = map[bucketId] || bucketId;
    setQueue(next);
    setExpandedOrderId(null);
    if (typeof window !== 'undefined') {
      const queueEl = document.querySelector('[data-testid="action-queue-smart-queue"]');
      if (queueEl) queueEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const refresh = () => setRefreshTick((t) => t + 1);

  // iter114s.99 — Order Management modal from queue rows (user msg 403).
  // Rows carry slim priority-engine fields; fetch the full order doc on open.
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [orderStatus, setOrderStatus] = useState('');

  const openOrderModal = async (row) => {
    try {
      const res = await axios.get(`${API_URL}/api/enhanced-orders/${row.id}`, { withCredentials: true });
      const o = res.data;
      setSelectedOrder(o);
      setTrackingNumber(o.tracking_number || '');
      setOrderStatus(o.status || '');
      setProofFile(null);
      setShowOrderModal(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to load order details.');
    }
  };
  const closeOrderModal = () => { setShowOrderModal(false); setSelectedOrder(null); };

  const handleUploadProof = async () => {
    if (!proofFile || !selectedOrder) return;
    const fd = new FormData();
    fd.append('order_id', selectedOrder.id);
    fd.append('file', proofFile);
    try {
      await axios.post(`${API_URL}/api/files/upload-proof`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      toast.success('Proof uploaded.');
      refresh();
      closeOrderModal();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Proof upload failed.');
    }
  };

  const handleUpdateOrderStatus = async () => {
    if (!selectedOrder) return;
    try {
      await axios.put(`${API_URL}/api/enhanced-orders/${selectedOrder.id}/status`, {
        status: orderStatus,
        tracking_number: trackingNumber || null,
      }, { withCredentials: true });
      toast.success('Order updated.');
      refresh();
      closeOrderModal();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed.');
    }
  };

  const headerCounts = useMemo(() => {
    if (!data) return { urgentTotal: 0, total: 0 };
    const urgent = data.priority_buckets
      .filter((b) => b.severity === 'critical')
      .reduce((acc, b) => acc + b.count, 0);
    return {
      urgentTotal: urgent,
      total: data.smart_queue?.total ?? 0,
    };
  }, [data]);

  return (
    <AdminLayout fullBleed>
      <div
        className="min-h-screen"
        data-testid="admin-action-queue-page"
      >
        {/* Sticky cockpit header — universal quick-actions + urgent count */}
        <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#6FBF73]/30 shadow-sm">
          <div className="max-w-screen-2xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-[#2F5D3A] text-white grid place-items-center font-black shadow">
                ⚡
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-[#14281D] leading-tight" data-testid="action-queue-heading">
                  Operational Priority Engine
                </h1>
                <p className="text-xs text-[#1F3D2C]/70">
                  {headerCounts.urgentTotal > 0 ? (
                    <span className="text-red-600 font-semibold">
                      {headerCounts.urgentTotal} urgent task{headerCounts.urgentTotal === 1 ? '' : 's'}
                    </span>
                  ) : (
                    <span>All critical queues clear ✓</span>
                  )}
                  <span className="text-gray-400 mx-2">|</span>
                  {headerCounts.total} order{headerCounts.total === 1 ? '' : 's'} in current view
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={refresh}
                className="text-xs font-semibold text-[#2F5D3A] bg-[#E8F1E6] hover:bg-[#D7E8D9] px-3 py-2 rounded-full transition"
                data-testid="action-queue-refresh"
              >
                ↻ Refresh
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-screen-2xl mx-auto px-6 py-8 space-y-8">
          {error && (
            <div
              className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700"
              data-testid="action-queue-error"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* ───── Section 1 — Priority Buckets ───── */}
          <section data-testid="action-queue-priority-buckets">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#2F5D3A] mb-3">
              Requires Immediate Action
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              {(data?.priority_buckets || Array.from({ length: 6 })).map((b, i) => (
                <PriorityBucketCard
                  key={b?.id || `skeleton-${i}`}
                  bucket={b}
                  isActive={b && (
                    (b.id === 'new_paid' && queue === 'new')
                    || (b.id === queue)
                  )}
                  loading={loading && !data}
                  onClick={() => b && handleBucketClick(b.id)}
                />
              ))}
            </div>
          </section>

          {/* ───── Section 2 — Live Production Pipeline ───── */}
          <section data-testid="action-queue-pipeline">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#2F5D3A] mb-3">
              Live Production Pipeline
            </h2>
            <LivePipelineKanban
              stages={data?.pipeline || []}
              loading={loading && !data}
              queueRows={data?.smart_queue?.rows || []}
              onStageChange={refresh}
            />
          </section>

          {/* ───── Section 3 — Universal Smart Queue ───── */}
          <section data-testid="action-queue-smart-queue" className="scroll-mt-24">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#2F5D3A]">
                Smart Queue
              </h2>
              <span className="text-xs text-[#1F3D2C]/60">
                Sorted by priority score · newest first
              </span>
            </div>
            <SavedViewsBar
              currentQueue={queue}
              onPickQueue={(q) => { setQueue(q); setExpandedOrderId(null); }}
            />
            <SmartQueueFilterChips current={queue} onChange={(c) => { setQueue(c); setExpandedOrderId(null); }} />
            {loading && !data ? (
              <div className="bg-white rounded-2xl shadow-sm border border-[#E5E7EB] p-12 grid place-items-center">
                <Loader2 className="w-7 h-7 text-[#6FBF73] animate-spin" />
              </div>
            ) : data?.smart_queue?.rows?.length ? (
              <div className="space-y-3" data-testid="action-queue-rows">
                {data.smart_queue.rows.map((row) => (
                  <MissionControlOrderRow
                    key={row.id}
                    row={row}
                    expanded={expandedOrderId === row.id}
                    onToggle={() => setExpandedOrderId((id) => (id === row.id ? null : row.id))}
                    onAction={refresh}
                    onOpenModal={() => openOrderModal(row)}
                  />
                ))}
              </div>
            ) : (
              <div
                className="bg-white rounded-2xl shadow-sm border border-[#E5E7EB] p-12 text-center"
                data-testid="action-queue-empty"
              >
                <div className="text-4xl mb-3">✨</div>
                <p className="text-[#14281D] font-semibold">Queue clear — nothing matching this filter.</p>
                <p className="text-sm text-[#1F3D2C]/60 mt-1">Pick another chip or click a Priority Bucket above.</p>
              </div>
            )}
          </section>
        </main>
      </div>
      <OrderManagementModal
        open={showOrderModal}
        order={selectedOrder}
        proofFile={proofFile}
        trackingNumber={trackingNumber}
        orderStatus={orderStatus}
        onClose={closeOrderModal}
        onProofFileChange={setProofFile}
        onUploadProof={handleUploadProof}
        onTrackingChange={setTrackingNumber}
        onStatusChange={setOrderStatus}
        onUpdateStatus={handleUpdateOrderStatus}
      />
    </AdminLayout>
  );
};

export default ActionQueue;
